import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchAthleteDeals, openAthleteThread } from "./athleteServices";
import { EMPTY_VALUE, athleteDealAction, dealStatusMeta, filterDeals, fmtMoney } from "./athleteUtils";
import { fmtDate } from "../../ForBrands/brandDealUtils";
import { Badge, Btn, Card, Chip, ErrorState, InsightBox, Loading, SearchBox } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import DealsMobile from "./mobile/DealsMobile";

// Athlete Mode · My Deals (Figma 04 · My Deals): filter chips, search, the
// deals table and the Cache AI value analysis. Below 640px (the phone
// theme) the same data renders as DealsMobile: deal cards instead of the
// table and the analysis figures as a stat list (Figma 2000:899).

const FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "declined", label: "Declined" },
];

const AthleteDealsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [opening, setOpening] = useState(null);
  const phone = usePhoneTheme();

  useEffect(() => {
    fetchAthleteDeals()
      .then((payload) => {
        setData(payload);
        setError(null);
      })
      .catch((err) => setError(err.message || "Could not load your deals"));
  }, []);

  const rows = useMemo(() => filterDeals(data?.deals, filter, search), [data, filter, search]);

  const message = async (deal) => {
    setOpening(deal.id);
    try {
      const thread = await openAthleteThread(deal.id);
      navigate(`/athlete/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    } finally {
      setOpening(null);
    }
  };

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { counts, analysis } = data;
  const summary = `${counts.all} total deal${counts.all === 1 ? "" : "s"} · ${counts.active} active${
    counts.pending ? ` · ${counts.pending} waiting on you` : ""
  }`;
  const emptyText = data.deals.length
    ? "No deals match these filters."
    : "No offers yet. Brands find you through Discover, so keep your profile and socials current.";
  const insightTitle = "Cache AI value analysis";
  const insightText = analysis.dealCount
    ? `You have earned ${fmtMoney(analysis.earned)} on Cache across ${analysis.dealCount} deal${analysis.dealCount === 1 ? "" : "s"}, averaging ${fmtMoney(analysis.avgDealValue)} per deal, with ${fmtMoney(analysis.dealVolume30d)} in deal volume over the last 30 days.`
    : "Accept your first offer to unlock deal value benchmarks. Countering within your NILx rate range keeps brands engaged.";
  // The InsightBox tiles on desktop, the StatList rows on phones.
  const valueStats = [
    {
      label: "Avg. deal value",
      value: analysis.avgDealValue == null ? EMPTY_VALUE : fmtMoney(analysis.avgDealValue),
      sub: analysis.dealCount ? `across ${analysis.dealCount} accepted deal${analysis.dealCount === 1 ? "" : "s"}` : "no accepted deals yet",
    },
    { label: "Deal volume", value: fmtMoney(analysis.dealVolume30d), sub: "last 30 days" },
  ];

  if (phone) {
    return (
      <DealsMobile
        summary={summary}
        filters={FILTERS}
        filter={filter}
        onFilter={setFilter}
        search={search}
        onSearch={setSearch}
        rows={rows}
        emptyText={emptyText}
        opening={opening}
        onMessage={message}
        insightTitle={insightTitle}
        insightText={insightText}
        stats={valueStats}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-[34px]">
      <div className="flex flex-col gap-[6px]">
        <h1 className="bm-h-l text-bm-ink">Your Deals</h1>
        <p className="bm-body-l text-bm-muted">{summary}</p>
      </div>

      <Card padding="p-5" className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <Chip key={f.value} active={filter === f.value} onClick={() => setFilter(f.value)} className="!h-8 !pl-[14px] !pr-3 bm-label-xs">
              {f.label}
            </Chip>
          ))}
        </div>
        <SearchBox aria-label="Search deals" value={search} onChange={setSearch} placeholder="Search campaign or brand" subtle className="w-full sm:w-[240px] !rounded-full" />
      </Card>

      <Card padding="p-0" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="bg-bm-subtle">
                <th className="bm-th pl-6 text-left">Campaign &amp; brand</th>
                <th className="bm-th text-left">Value</th>
                <th className="bm-th text-left">Status</th>
                <th className="bm-th text-left">Deadline</th>
                <th className="bm-th pr-6 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center bm-body-m text-bm-muted">
                    {emptyText}
                  </td>
                </tr>
              )}
              {rows.map((d) => {
                const meta = dealStatusMeta(d.status);
                const action = athleteDealAction(d);
                return (
                  <tr key={d.id} className="border-t border-bm-border">
                    <td className="bm-td pl-6">
                      <span className="flex flex-col gap-[2px]">
                        <span className="bm-h-xs text-bm-ink">{d.campaignName || "Untitled campaign"}</span>
                        <span className="bm-body-s text-bm-muted">{d.brand?.company}</span>
                      </span>
                    </td>
                    <td className="bm-td bm-num-m text-bm-ink">{fmtMoney(d.value, { cents: true })}</td>
                    <td className="bm-td">
                      <Badge tone={meta.tone} dot={meta.dot}>
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="bm-td text-bm-ink">{d.deadline ? fmtDate(d.deadline) : EMPTY_VALUE}</td>
                    <td className="bm-td pr-6">
                      <span className="flex items-center justify-end gap-2">
                        <Btn variant="secondary" size="sm" onClick={() => navigate(action.href)}>
                          {action.label} →
                        </Btn>
                        <Btn variant="secondary" size="sm" onClick={() => message(d)} disabled={opening === d.id}>
                          {opening === d.id ? "Opening..." : "Send message"}
                        </Btn>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="h-16 bg-bm-subtle" />
      </Card>

      <InsightBox title={insightTitle} tiles={valueStats}>
        {insightText}
      </InsightBox>
    </div>
  );
};

export default AthleteDealsPage;
